import type { RequestExample } from '@core/entities/RequestExample';
import type {
  CreateRequestExamplePayload,
  RequestExampleRepository,
} from '@core/repositories/RequestExampleRepository';
import { httpClient } from '@infrastructure/http/httpClient';
import { toAppError } from '@infrastructure/http/errorMapper';

interface RequestExampleResponse {
  id: string;
  request_id: string | null;
  name: string;
  description: string | null;
  status_code: number;
  status_text: string | null;
  response_headers: Record<string, string[]> | null;
  response_body: string | null;
  position: number;
  created_by: { id: string; name: string } | null;
  created_at: string;
  updated_at: string;
}

const toRequestExample = (payload: RequestExampleResponse): RequestExample => ({
  id: payload.id,
  requestId: payload.request_id,
  name: payload.name,
  description: payload.description,
  statusCode: payload.status_code,
  statusText: payload.status_text,
  responseHeaders: payload.response_headers,
  responseBody: payload.response_body,
  position: payload.position,
  createdBy: payload.created_by,
  createdAt: payload.created_at,
  updatedAt: payload.updated_at,
});

export class RequestExampleRepositoryImpl implements RequestExampleRepository {
  async list(requestId: string): Promise<RequestExample[]> {
    try {
      const response = await httpClient.get<{ data: RequestExampleResponse[] }>(
        `/requests/${requestId}/examples`,
      );
      return response.data.data.map(toRequestExample);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async create(requestId: string, payload: CreateRequestExamplePayload): Promise<RequestExample> {
    try {
      const response = await httpClient.post<{ data: RequestExampleResponse }>(
        `/requests/${requestId}/examples`,
        {
          name: payload.name,
          description: payload.description,
          status_code: payload.statusCode,
          status_text: payload.statusText,
          response_headers: payload.responseHeaders,
          response_body: payload.responseBody,
        },
      );
      return toRequestExample(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async rename(id: string, name: string, description: string | null): Promise<RequestExample> {
    try {
      const response = await httpClient.put<{ data: RequestExampleResponse }>(`/examples/${id}`, {
        name,
        description,
      });
      return toRequestExample(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async remove(id: string): Promise<void> {
    try {
      await httpClient.delete(`/examples/${id}`);
    } catch (error) {
      throw toAppError(error);
    }
  }
}
